import type { TranscriptSegment } from '@ai-meeting/shared'
import type { RealtimeToken } from '@soniox/client'

interface PersistableRealtimeToken extends RealtimeToken {
  speaker_name?: string
  translation_latency_ms?: number
}

function isSourceToken(token: RealtimeToken): boolean {
  return token.translation_status !== 'translation' && token.text !== '<end>'
}

function isTranslationToken(token: RealtimeToken): boolean {
  return token.translation_status === 'translation' && token.text !== '<end>'
}

function tokenTimestamp(token: RealtimeToken, fallbackMs: number): number {
  return typeof token.start_ms === 'number' ? fallbackMs + token.start_ms : fallbackMs
}

/**
 * The meeting offset to store beside a wall-clock timestamp, when there is one.
 *
 * `start_ms` is already counted from the meeting's origin, and in a shared room
 * that origin is the server's rather than this machine's — it hands a late
 * joiner the room's clock on `ready` (`timeline_seed_ms`). Storing it is what
 * lets the review screen label a line without inventing a zero of its own.
 * Omitted rather than guessed when the token carried no time at all.
 */
function elapsedField(startMs: number | undefined): { elapsedMs?: number } {
  return typeof startMs === 'number' ? { elapsedMs: startMs } : {}
}

function tokenLatency(token: RealtimeToken): number | undefined {
  const latency = (token as PersistableRealtimeToken).translation_latency_ms
  return typeof latency === 'number' ? latency : undefined
}

function tokenSpeakerFields(
  token: RealtimeToken
): Pick<TranscriptSegment, 'speaker' | 'speakerId' | 'speakerName'> {
  const speakerId = String(token.speaker ?? '1')
  const speakerName = (token as PersistableRealtimeToken).speaker_name?.trim()
  return speakerName
    ? { speaker: speakerName, speakerId, speakerName }
    : { speaker: speakerId }
}

/**
 * Convert source tokens into one transcript segment per token, preserving the
 * speaker/text/timestamp exactly as the STT stream emitted them.
 *
 * @param tokens        Raw token stream (may include partials, translations, sentinels).
 * @param startedAtMs   Wall-clock anchor used to convert relative `start_ms` to absolute timestamps.
 */
export function buildSourceSegments(
  tokens: readonly RealtimeToken[],
  startedAtMs: number
): TranscriptSegment[] {
  const finalTokens = tokens.filter((token) => token.is_final && token.text !== '<end>')
  return finalTokens
    .filter(isSourceToken)
    .map((token, index): TranscriptSegment => {
      const timestamp =
        typeof token.start_ms === 'number'
          ? tokenTimestamp(token, startedAtMs)
          : startedAtMs + index
      return {
        id: `source-${index}-${timestamp}`,
        ...tokenSpeakerFields(token),
        text: token.text,
        timestamp,
        ...elapsedField(token.start_ms),
        isFinal: true,
      }
    })
    .filter((segment) => segment.text.trim())
}

/**
 * Convert translation tokens into one transcript segment per token. The text
 * field carries the translated content verbatim — translations are NOT merged
 * back into source segments, so downstream consumers see exactly what the
 * socket emitted.
 *
 * @param tokens        Raw token stream (translations are filtered internally).
 * @param startedAtMs   Wall-clock anchor used to convert relative `start_ms` to absolute timestamps.
 */
export function buildTranslationSegments(
  tokens: readonly RealtimeToken[],
  startedAtMs: number
): TranscriptSegment[] {
  const finalTokens = tokens.filter((token) => token.is_final && token.text !== '<end>')
  return finalTokens
    .filter(isTranslationToken)
    .map((token, index): TranscriptSegment => {
      const timestamp =
        typeof token.start_ms === 'number'
          ? tokenTimestamp(token, startedAtMs)
          : startedAtMs + index
      const latency = tokenLatency(token)
      return {
        id: `translation-${index}-${timestamp}`,
        ...tokenSpeakerFields(token),
        text: token.text,
        timestamp,
        ...elapsedField(token.start_ms),
        isFinal: true,
        ...(latency != null ? { translation_latency_ms: latency } : {}),
      }
    })
    .filter((segment) => segment.text.trim())
}

/**
 * Backward-compatible wrapper that returns only source segments. Prefer
 * `buildSourceSegments` + `buildTranslationSegments` directly.
 *
 * @deprecated Use `buildSourceSegments` / `buildTranslationSegments` for the
 *             verbatim socket pipeline. Kept so older callers still compile.
 */
export function buildTranscriptFromTokenGroups(
  tokens: readonly RealtimeToken[],
  startedAtMs: number
): TranscriptSegment[] {
  return buildSourceSegments(tokens, startedAtMs)
}
